import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import axios from 'axios';
import {getHeaders, TEMP_DATASERVICE_HEADERS} from '.';
import URL_CONST from '../../config/url';
import {
  Dataset,
  DatasetCreateResponse,
  DatasetResponse,
} from '../../types/dataService/Dataset';
import {DatasetMetadataLabelItem} from '../../types/dataService/DatasetMetadata';
import {DataPrepareType} from '../../types/dataset/DatasetGetAnnotationsResponse';

export type LabelMap = {[id: string]: DatasetMetadataLabelItem[]};

export type DatasetDistribution = {
  images?: number; //binary/image
  videos?: number; //binary/video
  labels?: number; //annotation/label
};

export type DatasetStatisticValue = {[key: string]: number};
export type DatasetCategoryStatistics = {
  UNSPLIT?: DatasetStatisticValue;
  SPLIT?: DatasetStatisticValue;
};
export type DatasetOrphanStatistics = {
  binaries?: string[];
  annotations?: string[];
};

export type DatasetStatistics = {
  distribution?: DatasetDistribution;
  statistics?: {
    category?: DatasetCategoryStatistics;
    orphans?: DatasetOrphanStatistics;
  };
};

export type DatasetState = {
  isBusy: boolean;
  // TODO: cleanup since its on metadata now
  name?: string | null;
  description?: string | null;

  data?: Dataset;
  type: DataPrepareType | null;
} & DatasetStatistics;

const initialState: DatasetState = {
  isBusy: false,
  data: undefined,
  type: null,
};

// Create new dataset container
// TODO: input name should not be optional
export const createDataset = createAsyncThunk('dataset/create', async () => {
  const {data} = await axios.put<DatasetCreateResponse>(URL_CONST.DSS_DATASET, null, {
    headers: await getHeaders(),
  });
  return data.body;
});

export const assignDatasetToProject = createAsyncThunk(
  'dataset/assign',
  async (args: {projectId: string; externalId: string; externalVersion: number}) => {
    const {projectId, ...rest} = args;
    const {data} = await axios.post<DatasetCreateResponse>(
      URL_CONST.DATASET_ASSIGN(projectId),
      null,
      {
        params: {
          ...rest,
        },
      }
    );
    return data.body;
  }
);

// Get dataset details
export const getDataset = createAsyncThunk(
  'dataset/get',
  async (args: {datasetId: string; version?: number}) => {
    const {datasetId, version} = args;
    const {data} = await axios.get<DatasetResponse>(URL_CONST.DSS_DATASET, {
      headers: await getHeaders(TEMP_DATASERVICE_HEADERS),
      params: {
        datasetId,
        version,
      },
    });
    return data.body;
  }
);

// Delete dataset
export const deleteDataset = createAsyncThunk(
  'dataset/delete',
  async (datasetId: string) => {
    const {data} = await axios.delete<DatasetResponse>(URL_CONST.DSS_DATASET, {
      headers: await getHeaders(),
      params: {
        datasetId,
      },
    });
    return data.body;
  }
);

const getBinaryTotal = (data: Dataset) => {
  let binaryTotal = 0;
  for (const key in data.metadataDistribution) {
    binaryTotal += key.includes('binary') ? data.metadataDistribution[key] : 0;
  }
  return binaryTotal;
};

const setIsBusy = (state: DatasetState) => {
  state.isBusy = true;
};

const datasetSlice = createSlice({
  name: 'dataset',
  initialState,
  reducers: {
    setType(state, action: PayloadAction<DataPrepareType | null>) {
      state.type = action.payload;
    },
    updateDatasetId(state, action: PayloadAction<string>) {
      state.data = {
        ...(state.data as Dataset),
        id: action.payload,
      };
    },
    updateLocal(state, action: PayloadAction<Dataset>) {
      // Used when other ops ex. jobs returns an updated dataset stats which are still in WIP
      state.data = {
        ...state.data,
        ...action.payload,
        binaryTotal: getBinaryTotal(action.payload),
      };
    },
    updateStats(state, action: PayloadAction<DatasetStatistics>) {
      state.distribution = action.payload.distribution;
      state.statistics = action.payload.statistics;
    },
    reset() {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createDataset.pending, setIsBusy)
      .addCase(createDataset.fulfilled, (state, action) => {
        state.isBusy = false;
        state.data = action.payload;
      })
      .addCase(getDataset.pending, setIsBusy)
      .addCase(getDataset.fulfilled, (state, action) => {
        state.isBusy = false;
        const data = action.payload;
        state.data = {...data, binaryTotal: getBinaryTotal(data)};
      })
      .addCase(getDataset.rejected, (state, action) => {
        return initialState;
      })
      .addCase(deleteDataset.pending, setIsBusy)
      .addCase(deleteDataset.fulfilled, (state, action) => {
        state.isBusy = false;
      });
  },
});

export const {actions: datasetActions, reducer: datasetReducer} = datasetSlice;
